import { useState, useEffect, useMemo } from "react";
import { Container, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';// , Switch

import Header from './components/Header';
import Footer from './components/Footer';
import { ThemeModeContext } from "./contexts/ThemeModeContext";
import RoutesList from './constants/Routes';
import type { IRoute } from './interfaces/IRoute';

import './css/creacirque.scss';
import { CourseProvider } from './contexts/CourseContext';
import { StageProvider } from './contexts/StageContext';
import Home from './pages/Home';
import AdminStages from './pages/Admin/Stages';
// import NewStage from './pages/Admin/Stages/New';
import Courses from './pages/Courses';
import Stages from "./pages/Stages";

export default function App() {

  // Détecter le thème par défaut du navigateur
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  // Charger préférence utilisateur si existante, sinon fallback au système
  const readInitial = () => {
    const stored = localStorage.getItem("preferredThemeMode");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return mediaQuery.matches;
  };

  const [darkMode, setDarkMode] = useState<boolean>(readInitial);

  // Écouter les changements du thème système (si l’utilisateur n’a pas forcé une préférence)
  useEffect(() => {
    const handler = (e: MediaQueryListEvent) => {
      // Ne pas écraser une préférence explicite; ici on choisit de détecter si aucune clé en localStorage
      const stored = localStorage.getItem("preferredThemeMode");
      if (!stored) setDarkMode(e.matches);
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [mediaQuery]);

  // Exposer une action pour basculer le thème + persister
  const toggleTheme = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem("preferredThemeMode", next ? "dark" : "light");
      return next;
    });
  };

  // Créer le thème avec transition fluide
  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
        },
        // palette: {
        //   mode: darkMode ? "dark" : "light",
        //   background: {
        //     default: darkMode ? "var(--color-dark-bg)" : "var(--color-light-bg)",
        //     paper: darkMode ? "var(--color-dark-bg)" : "var(--color-light-bg)"
        //   },
        //   text: {
        //     primary: !darkMode ? "var(--color-dark-text)" : "var(--color-light-text)"
        //   }
        // },
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "rgba(50,0,50,.6)" : "rgba(255,255,255,.6)",
            paper: darkMode ? "rgba(50,0,50,.6)" : "rgba(255,255,255,.6)"
          },
          text: {
            primary: !darkMode ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)"
          }
        },
        components: {
          MuiTableCell: {
            styleOverrides: {
              root: {
                all: 'unset',
                display: 'table-cell',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                backgroundColor: darkMode ? 'rgba(0,128,0,1)' : 'rgba(0,128,0,1)',
                color: darkMode ? '#fff' : '#fff',
                // padding: '.9rem',
                borderRadius: '10px',
                fontSize: '1.2rem',
                // width: '150px',
                textTransform: 'none',
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                transition: "background-color 0.3s ease, color 0.3s ease",
              },
              "#content": {
                backgroundColor: darkMode ? 'rgba(50,0,50,.6)' : 'rgba(255,255,255,.6)',
                color: darkMode ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)',
                transition: "background-color 0.3s ease, color 0.3s ease",
              }
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: () => ({
                backgroundColor: darkMode ? "#fff" : "#fff", // ici même couleur
                borderRadius: 4,
                transition: "background-color 0.3s ease, color 0.3s ease",
                '& .MuiInputBase-input': {
                  color: darkMode ? "#000" : "#000",
                  transition: "color 0.3s ease",
                  // '&::placeholder': {
                  //   color: darkMode ? "#666" : "#666",
                  //   opacity: 1,
                  // },
                },
              }),
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                color: darkMode ? "#666" : "#666",
                transition: "color 0.3s ease",
                "&.Mui-focused": {
                  color: darkMode ? "#000" : "#000",
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              notchedOutline: {
                borderColor: darkMode ? "#fff" : "#fff",
                transition: "border-color 0.3s ease",
              },
              root: {
                 "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: darkMode ? "#999" : "#999",
                  },
                backgroundColor: darkMode ? "#fff" : "#fff", // même couleur que TextField
                borderRadius: 4,
                transition: "background-color 0.3s ease, color 0.3s ease",
                '& .MuiInputBase-input': {
                  color: darkMode ? "#000" : "#000",
                  transition: "color 0.3s ease",
                  '&::placeholder': {
                    color: darkMode ? "#AAA" : "#AAA",
                    opacity: 1,
                  },
                },
              },
            },
          },
          // MuiInputBase: {
          //   styleOverrides: {
          //     input: ({ theme }) => ({
          //       '::placeholder': {
          //         color: theme.palette.mode === "dark" ? "#F00" : "#00F",
          //         opacity: 1,
          //       },
          //     }),
          //   }
          // }
        },
      }),
    [darkMode]
  );

  const themeModeContextValue = useMemo(
    () => ({ darkMode, setDarkMode, toggleTheme }),
    [darkMode]
  );

  return (
    <div id="app">
      <Router>
        <ThemeModeContext.Provider value={themeModeContextValue} >
          <ThemeProvider theme={theme}>
            <CssBaseline />
            {/* <Container sx={{ textAlign: "center", mt: 4 }}> */}
            <Container maxWidth={false} disableGutters>
              <Header />
              <div id="page">
                <Routes>
                  {RoutesList.map((route: IRoute) => <Route path={route.url} key={route.url} element={<route.comp />} />)}
                  <Route path="/" key="/" element={
                    <StageProvider> {/* Routes utilisant la liste des cours via API */}
                      <Home />
                    </StageProvider>
                  }>
                  </Route>
                  <Route path="/les-cours" key="les-cours" element={
                    <CourseProvider> {/* Routes utilisant la liste des cours via API */}
                      <Courses />
                    </CourseProvider>
                  }>
                  </Route>
                  <Route path="/stages-week-ends" key="stages-week-ends" element={
                    <StageProvider> {/* Routes utilisant la liste des cours via API */}
                      <Stages />
                    </StageProvider>
                  }>
                  </Route>
                  <Route path="/admin-stages" key="admin-stages" element={
                    <StageProvider> {/* Routes utilisant la liste des cours via API */}
                      <AdminStages />
                    </StageProvider>
                  }>
                  </Route>
                  {/* <Route path="/admin-stages/new" key="admin-stages-new" element={
                    <StageProvider> 
                      <NewStage />
                    </StageProvider>
                  }>
                  </Route> */}
                </Routes>
              </div>
              {/* <Legend /> */}
              {/* <NewLegend /> */}
              {/* <Footnote /> */}
            </Container>
            {/* Footer en bas à droite */}
            <Footer />

          </ThemeProvider>
        </ThemeModeContext.Provider>
      </Router>
    </div>
  );
}